import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { Colors } from 'theme';
import 'components/Charts/NewDonutChart/styles.css';

const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 300;
const DEFAULT_MARGIN = 50;
const INNER_RADIUS = 50;
const DEFAULT_LEGEND_GROUP = 3;
const DEFAULT_DONUT_WIDTH = 300;
const DEFAULT_FONT_SIZE = '1rem';
const DEFAULT_BAR_COLOR = Colors.primaryLight;
const CHART_PERCENTAGE = '100.0%';
const POSITION_ALIGN_VAL = 30;
const POSITION_SPACE_VAL = 3;

/**
 * NewDonutChart
 * Creates and renders the NewDonutChart component using the d3 library.
 * `id` required property to instance the component and draw the chart.
 * `data` required property to instance the component and draw the chart.
 * `barColors` required property to instance the component and draw the chart.
 */
export class NewDonutChart extends Component {
  /**
   * @method findBarColor
   * Finds the bar color assigned in the data object
   * into the barColors array to fill the donut bars.
   * @param item The object item from data to review
   * @param barColors The colors collection to compare.
   * @return {string}
   */
  static findBarColor = (item, barColors = []) => {
    const barColor = barColors.find(
      (color) => color.name.toUpperCase() === item.name.toUpperCase(),
    );
    return barColor ? barColor.color : DEFAULT_BAR_COLOR;
  };

  constructor() {
    super();
    this.chartRef = React.createRef();
  }

  componentDidMount() {
    this.renderChart();
  }

  /**
   * @method getGraphText
   * Gets the text to display in the chart bar based on the value provided.
   * @param item The data item to display in the chart.
   * @return {string}
   */
  static getGraphText = (item) => (item.value > 0 && item.displayValue ? `${item.name}` : '');

  static getGraphValue = (item) =>
    item.value > 0 && item.displayValue ? `${item.displayValue}` : '';

  /**
   * Renders Pie Chart
   */
  renderChart() {
    const {
      data = {},
      width = DEFAULT_WIDTH,
      donutWidth = DEFAULT_DONUT_WIDTH,
      height = DEFAULT_HEIGHT,
      margin = DEFAULT_MARGIN,
      innerRadius = INNER_RADIUS,
      hideLegends = false,
      legendGroup = DEFAULT_LEGEND_GROUP,
      legendToSide = false,
      hideSliceLabels = false,
    } = this.props;

    const { barColors } = this.props;

    // shift x-alignment of donut if legend shows next to it
    const xAlignment = legendToSide ? 4 : 2;
    // boost height of the donut if legend shows below it
    const yAlignment = legendToSide ? height / 2 : 130;

    // Clear any previous svg if this ever re-renders
    d3.select(this.chartRef.current).select('svg').remove();

    // Creates sources <svg> element
    const svg = d3
      .select(this.chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('class', 'chart');

    const graph = svg
      .append('g')
      .attr('transform', `translate(${width / xAlignment}, ${yAlignment})`);

    const radius = Math.min(donutWidth - margin, height - margin) / 2;

    const arc = d3
      .arc()
      .outerRadius(radius - 10)
      .innerRadius(innerRadius);

    const pie = d3.pie().value((d) => d.value);

    const arcs = graph
      .selectAll('slices')
      .data(pie(data))
      .join((enter) => enter.append('path').attr('class', 'arc').style('stroke', 'white'));

    arcs.attr('d', arc).style('fill', (d) => NewDonutChart.findBarColor(d.data, barColors));

    // ------------------------------------------------------------
    // Labels + polylines (callouts) — render ONLY when requested
    // ------------------------------------------------------------
    if (!hideSliceLabels) {
      // Slice labels (name + value)
      graph
        .selectAll('slices')
        .data(pie(data))
        .enter()
        .append('text')
        .text((d) => NewDonutChart.getGraphText(d.data))
        .attr('transform', (d) => {
          const pos = arc.centroid(d);
          const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          if (d.index === 1) {
            pos[1] += POSITION_ALIGN_VAL;
          }
          pos[0] = radius * 1.3 * (midangle < Math.PI ? 1 : -1);
          return `translate( ${pos} )`;
        })
        .style('font-size', '13px')
        .style('font-weight', 800)
        .style('text-anchor', (d) => {
          const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          return midangle < Math.PI ? 'start' : 'end';
        })
        .append('tspan')
        .attr('x', (d) => {
          const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          if (midangle < Math.PI) return 0;
          return d.data.displayValue === CHART_PERCENTAGE ? -20 : -40;
        })
        .attr('dy', '1.3em')
        .style('text-anchor', (d) => {
          const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          return midangle < Math.PI ? 'start' : 'end';
        })
        .text((d) => NewDonutChart.getGraphValue(d.data));

      // Callout polylines
      graph
        .selectAll('allPolylines')
        .data(pie(data))
        .enter()
        .append('polyline')
        .attr('stroke', 'black')
        .style('fill', 'none')
        .attr('stroke-width', 1)
        .attr('points', (d) => {
          if (d.data.value !== 0) {
            const posA = arc.centroid(d);
            const posB = arc.centroid(d);
            const posC = arc.centroid(d);
            const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
            posC[0] = radius * 0.95 * (midangle < Math.PI ? 1.3 : -1.3);
            if (d.index === 1) {
              posA[0] += POSITION_SPACE_VAL;
              posA[1] += POSITION_ALIGN_VAL;
              posB[1] += POSITION_ALIGN_VAL;
              posC[1] += POSITION_ALIGN_VAL;
            }
            return [posA, posB, posC];
          }
          return null;
        });
    }

    // ------------------------------------------------------------
    // Legend
    // ------------------------------------------------------------
    let rowIndex = 0;
    let colIndex = 0;

    /**
     * displayValuePadding
     * @param {number} length input length of the value for which padding will be calculated.
     * returns number of px needed to pad that number. (horiz padding)
     */
    const displayValuePadding = (length) => {
      let len;
      switch (length) {
        case 4: // example: 0.4%
          len = 10;
          break;
        case 5: // example: 43.5%
          len = 5;
          break;
        default:
          // example: 100.0% or any other length
          len = 0;
          break;
      }
      return len;
    };

    if (!hideLegends) {
      for (let idxGroup = 0, len = data.length; idxGroup < len; idxGroup += 1) {
        const dataIdxGroup = data[`${idxGroup}`];
        const barColor = NewDonutChart.findBarColor(dataIdxGroup, barColors);

        if (legendToSide) {
          // whether or not we show legends on the side or below the donut
          svg
            .append('rect')
            .attr('x', 350)
            .attr('y', height / 2 - data.length * 7.5 + idxGroup * 15)
            .text(dataIdxGroup.name)
            .style('font-size', DEFAULT_FONT_SIZE)
            .attr('width', 10)
            .attr('height', 10)
            .attr('fill', barColor);

          svg
            .append('text')
            .attr('x', 365)
            .attr('y', height / 2 - data.length * 7.5 + idxGroup * 15 + 5)
            .text(dataIdxGroup.name)
            .style('font-size', DEFAULT_FONT_SIZE)
            .attr('alignment-baseline', 'middle');

          svg
            .append('text')
            .attr(
              'x',
              500 +
                displayValuePadding(String(dataIdxGroup.displayValue || dataIdxGroup.value).length),
            )
            .attr('y', height / 2 - data.length * 7.5 + idxGroup * 15 + 5)
            .text(hideSliceLabels ? dataIdxGroup.displayValue : dataIdxGroup.value)
            .style('font-size', DEFAULT_FONT_SIZE)
            .attr('alignment-baseline', 'middle');
        } else {
          svg
            .append('rect')
            .attr('x', margin / 2 + colIndex * (width / legendGroup))
            .attr('y', height - (margin - 20) + rowIndex * 15)
            .text(dataIdxGroup.name)
            .style('font-size', DEFAULT_FONT_SIZE)
            .attr('width', 10)
            .attr('height', 10)
            .attr('fill', barColor);

          svg
            .append('text')
            .attr('x', margin / 2 + colIndex * (width / legendGroup) + 15)
            .attr('y', height - (margin - 25) + rowIndex * 15)
            .text(dataIdxGroup.name)
            .style('font-size', DEFAULT_FONT_SIZE)
            .attr('alignment-baseline', 'middle');

          svg
            .append('text')
            .attr('x', margin / 2 + colIndex * (width / legendGroup) + 25)
            .attr('y', height - (margin - 25) + rowIndex * 15 + 15)
            .text(dataIdxGroup.value)
            .style('font-size', DEFAULT_FONT_SIZE)
            .attr('alignment-baseline', 'middle');
        }

        colIndex += 1;
        if (colIndex === legendGroup) {
          colIndex = 0;
          rowIndex += 1;
        }
      }
    }
  }

  render() {
    const { id = 'NewDonutChart' } = this.props;
    const backgroundColor = 'var(--color-bg)';
    return (
      <div
        id={id}
        ref={this.chartRef}
        style={{ backgroundColor }}
        data-testid="donut-chart-component"
      />
    );
  }
}

NewDonutChart.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  height: PropTypes.number,
  width: PropTypes.number,
  margin: PropTypes.number,
  innerRadius: PropTypes.number,
  hideLegends: PropTypes.bool,
  id: PropTypes.string,
  legendGroup: PropTypes.number,
  donutWidth: PropTypes.number,
  barColors: PropTypes.array.isRequired,
  legendToSide: PropTypes.bool,
  hideSliceLabels: PropTypes.bool,
};

export default NewDonutChart;
